/**
 * Stat modifiers: the two shapes the registries use, and how each becomes a number.
 *
 * Walking every entry of the 2.0.2 snapshot turns up 5,747 modifier objects and exactly two
 * key signatures:
 *
 *   `{ type, stat, min, max }`            3,642  affixes, gear bases, uniques, spells,
 *                                                support gems, runes, runewords, auras,
 *                                                exile effects, map/mob affixes, omens
 *   `{ type, stat, v1, scale_to_lvl }`    2,105  perks, base stats, gems, entities,
 *                                                `core_stat_data`
 *
 * The first is `StatModifier` — a range an item rolls within. The second is `OptScaleExactStat`
 * — a fixed value that may or may not scale with the holder's level. Both end up as an
 * `ExactStatData`, which is what the container consumes.
 */
/**
 * `ModType`. Note the resolution rule, which the pack leans on:
 *
 *     for (ModType type : ModType.values()) {
 *         if (type.id.toLowerCase(Locale.ROOT).equals(str.toLowerCase(Locale.ROOT))) { return type; }
 *     }
 *     try { ModType TYPE = valueOf(str); ... } catch (IllegalArgumentException e) { ... }
 *     return FLAT;
 *
 * — ModType.java:37-54. Case-insensitive, and **anything unrecognised becomes FLAT** rather
 * than erroring. This pack ships 281 `"flat"`, 55 `"percent"` and one `"more"` in lowercase
 * alongside the uppercase spellings, sometimes in the same `stats` array.
 */
export const MOD_TYPES = ["FLAT", "PERCENT", "MORE"];
export function modTypeFromString(raw) {
    const upper = raw.toUpperCase();
    return MOD_TYPES.includes(upper) ? upper : "FLAT";
}
/** Whether a modifier type was spelled in a way the game recognises at all. */
export function isKnownModType(raw) {
    return MOD_TYPES.includes(raw.toUpperCase());
}
export function parseRolledMod(node) {
    const statId = node["stat"];
    const type = node["type"];
    const min = node["min"];
    const max = node["max"];
    if (typeof statId !== "string" || typeof type !== "string")
        return undefined;
    if (typeof min !== "number" || typeof max !== "number")
        return undefined;
    return { statId, type: modTypeFromString(type), min, max };
}
export function parseSourceMod(node) {
    const statId = node["stat"];
    const type = node["type"];
    const v1 = node["v1"];
    if (typeof statId !== "string" || typeof type !== "string" || typeof v1 !== "number") {
        return undefined;
    }
    return {
        statId,
        type: modTypeFromString(type),
        v1,
        scaleToLvl: node["scale_to_lvl"] === true,
    };
}
/** Reads every modifier of one shape out of a raw `stats`-style array. */
export function parseRolledMods(nodes) {
    return nodes.map(parseRolledMod).filter((m) => m !== undefined);
}
export function parseSourceMods(nodes) {
    return nodes
        .filter((n) => n !== null && typeof n === "object" && !Array.isArray(n))
        .map(parseSourceMod)
        .filter((m) => m !== undefined);
}
/**
 * Level scaling.
 *
 *     public final float scale(ModType mod, float stat, float lvl) {
 *         if (mod.isFlat()) { return getScaling().scale(stat, lvl); }
 *         return stat;
 *     }
 *
 * — Stat.java:197-202. **Only FLAT scales.** A PERCENT or MORE modifier is the same number at
 * level 1 and level 100, which is why levelling a character raises flat life and armor but
 * never the "+20% increased" lines.
 */
export function levelScale(type, value, level, shape, bal) {
    if (type !== "FLAT")
        return value;
    return value * bal.multiFor(shape.scaling, level);
}
/**
 * A rolled modifier at a given roll percent and level.
 *
 *     data.v1 = (mod.min + (mod.max - mod.min) * percent / 100F);
 *     data.type = mod.getModType();
 *     data.stat = mod.stat;
 *     data.scaleToLevel(lvl);
 *
 * — `ExactStatData.fromStatModifier`, ExactStatData.java:48-59.
 */
export function rollToExact(mod, rollPercent, level, shape, bal) {
    const raw = mod.min + ((mod.max - mod.min) * rollPercent) / 100;
    return { statId: mod.statId, type: mod.type, value: levelScale(mod.type, raw, level, shape, bal) };
}
/**
 * A fixed modifier at the holder's level.
 *
 * `scale_to_lvl` decides whether the level is the holder's or 1 — `BonusStatPerEffectStacks`
 * spells it out as `x.scale_to_lvl ? data.getLevel() : 1` (BonusStatPerEffectStacks.java:73-79),
 * and level 1 with any curve is a multiplier of `base_scaling`, i.e. 1 in this pack.
 */
export function sourceToExact(mod, level, shape, bal) {
    const lvl = mod.scaleToLvl ? level : 1;
    return { statId: mod.statId, type: mod.type, value: levelScale(mod.type, mod.v1, lvl, shape, bal) };
}
/** `ExactStatData.multiplyBy` — used where a source contributes at partial strength. */
export function multiplyExact(mod, multi) {
    return { ...mod, value: mod.value * multi };
}
//# sourceMappingURL=modifier.js.map